import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export default function Signup({ betaMode = false }) {
  const [subdomain, setSubdomain] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState("basic");
  const [isMobile, setIsMobile] = useState(false);
  const [betaLoading, setBetaLoading] = useState(true);
  const [betaControls, setBetaControls] = useState({
    workspace_creation_enabled: false,
    public_signup_enabled: false,
    request_early_access_enabled: true,
    beta_invite_mode: true,
    show_beta_disclaimer: true,
  });

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 760);
    }

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    async function loadBetaControls() {
      try {
        const res = await fetch("/api/platform/beta-controls/public");
        if (!res.ok) throw new Error("Failed to load beta controls.");
        const data = await res.json();
        setBetaControls(data);
      } catch (error) {
        console.error(error);
      } finally {
        setBetaLoading(false);
      }
    }

    loadBetaControls();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setMsg("");

    if (betaMode) {
      if (!betaControls.beta_program_enabled) {
        setMsg("JobTelesis Beta enrollment is currently closed.");
        return;
      }
    } else {
      if (!betaControls.public_signup_enabled) {
        setMsg("Public signup is currently closed. Please request early access or contact JobTelesis.");
        return;
      }

      if (!betaControls.workspace_creation_enabled) {
        setMsg("Workspace creation is temporarily unavailable. Please request early access or contact JobTelesis.");
        return;
      }
    }

    if (!company || !subdomain || !email || !password) {
      setMsg("Please complete company name, workspace subdomain, email, and password.");
      return;
    }

    setBusy(true);

    try {
      const res = await fetch(betaMode ? "/api/auth/beta-signup" : "/api/auth/signup-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company,
          subdomain,
          email,
          password,
          contact_person: contactPerson,
          contact_phone: contactPhone,
          business_type: businessType,
          plan,
        }),
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Signup failed (${res.status}): ${text}`);
      }

      const data = await res.json();

      if (!betaMode && data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }

      if (data?.tenant_slug) {
        localStorage.setItem("admin_mode", "1");
        localStorage.setItem("x-tenant", data.tenant_slug);
      }

      window.location.href = "/app/workspace-created";
    } catch (err) {
      setMsg(err.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  }

  const colors = {
    navy: "#071827",
    panel: "#10263a",
    panel2: "#13293d",
    border: "rgba(255,255,255,0.09)",
    gold: "#e0b84f",
    blue: "#00c2ff",
    text: "#ffffff",
    muted: "#9fb0c3",
  };

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    background: "#071827",
    color: colors.text,
    padding: "12px 14px",
    outline: "none",
    fontSize: 14,
  };

  const labelStyle = {
    display: "grid",
    gap: 6,
    color: "#d7e7f5",
    fontSize: 13,
    fontWeight: 700,
  };

  const helperStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    marginLeft: 6,
    borderRadius: "50%",
    background: "rgba(224,184,79,0.16)",
    border: "1px solid rgba(224,184,79,0.45)",
    color: colors.gold,
    fontSize: 11,
    fontWeight: 800,
    cursor: "help",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top left, rgba(0,194,255,0.16), transparent 34%), linear-gradient(135deg, #071827 0%, #0b1b2b 45%, #10263a 100%)",
        color: colors.text,
        padding: isMobile ? 14 : 24,
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? 14 : 16,
            marginBottom: isMobile ? 22 : 38,
          }}
        >
          <Link
            to="/"
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              color: colors.text,
              textDecoration: "none",
            }}
          >
            <div
              style={{
                width: isMobile ? 40 : 46,
                height: isMobile ? 40 : 46,
                borderRadius: 14,
                background: colors.gold,
                color: "#071827",
                display: "grid",
                placeItems: "center",
                fontWeight: 900,
                letterSpacing: 0.5,
              }}
            >
              JT
            </div>

            <div>
              <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800 }}>JobTelesis</div>
              <div style={{ color: colors.muted, fontSize: 13 }}>
                Operations software for contractors and service businesses
              </div>
            </div>
          </Link>

          <Link
            to="/login"
            style={{
              color: colors.blue,
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            Sign in →
          </Link>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
            gap: isMobile ? 16 : 28,
            alignItems: "stretch",
          }}
        >
          <section
            style={{
              background: "rgba(16,38,58,0.74)",
              border: `1px solid ${colors.border}`,
              borderRadius: isMobile ? 16 : 22,
              padding: isMobile ? 20 : 34,
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            <div
              style={{
                color: colors.gold,
                fontSize: 13,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: 1.2,
                marginBottom: 14,
              }}
            >
              Create your workspace
            </div>

            <h1
              style={{
                fontSize: isMobile ? 34 : 46,
                lineHeight: 1.05,
                margin: "0 0 18px",
                letterSpacing: -1.2,
              }}
            >
              Start your system the right way.
            </h1>

            <p
              style={{
                color: "#c9d8e8",
                fontSize: 17,
                lineHeight: 1.65,
                maxWidth: 640,
                marginBottom: 28,
              }}
            >
              Set up your company workspace and create the first owner account.
              JobTelesis is built to help contractors and service businesses run jobs,
              track customers, manage estimates, and stay on top of invoicing.
            </p>

            <div style={{ display: "grid", gap: 14 }}>
              {[
                [
                  "Your own company workspace",
                  "Create a dedicated environment for your business with its own identity and data.",
                ],
                [
                  "Your first admin account",
                  "The email and password you enter here become the owner login for the workspace.",
                ],
                [
                  "Built to grow",
                  "Start with a strong operational foundation, then expand your system as your business scales.",
                ],
              ].map(([title, body]) => (
                <div
                  key={title}
                  style={{
                    background: "rgba(7,24,39,0.72)",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 16,
                    padding: 16,
                  }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 5 }}>{title}</div>
                  <div style={{ color: colors.muted, lineHeight: 1.5, fontSize: 14 }}>
                    {body}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section
            style={{
              background: colors.panel2,
              border: `1px solid ${colors.border}`,
              borderRadius: 22,
              padding: 30,
              boxShadow: "0 24px 80px rgba(0,0,0,0.28)",
            }}
          >
            <h2 style={{ margin: "0 0 8px", fontSize: 28 }}>
              {betaMode ? "Join JobTelesis Beta" : "Create Workspace"}
            </h2>
            <p style={{ margin: "0 0 20px", color: colors.muted, lineHeight: 1.5 }}>
              Enter your company details below to create your JobTelesis workspace.
            </p>

            {msg && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(248,113,113,0.35)",
                  background: "rgba(248,113,113,0.08)",
                  color: "#fca5a5",
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              >
                {msg}
              </div>
            )}

            {betaControls.show_beta_disclaimer && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: "1px solid rgba(224,184,79,0.35)",
                  background: "rgba(224,184,79,0.10)",
                  color: "#f8e7a2",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                JobTelesis is currently in beta. Features may change, and users should review all estimates, invoices, receipts, and records before sending or relying on them.
              </div>
            )}

            {!betaControls.public_signup_enabled && !betaLoading && (
              <div
                style={{
                  marginBottom: 16,
                  padding: "12px 14px",
                  borderRadius: 14,
                  border: "1px solid rgba(96,165,250,0.35)",
                  background: "rgba(96,165,250,0.10)",
                  color: "#bfdbfe",
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                {betaMode
                  ? "JobTelesis Beta access is limited and controlled by Platform Headquarters."
                  : "Public workspace signup is currently closed. Beta access is being managed by invitation or early access request."}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: 14 }}>
              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Company name
                  <span
                    style={helperStyle}
                    title="This becomes the business name shown inside your workspace and may appear on estimates, invoices, receipts, and client documents."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  placeholder="DFASIO Holdings Corp"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                />
              </label>

              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Workspace subdomain
                  <span
                    style={helperStyle}
                    title="Choose a short and memorable workspace name. You will use this workspace name to sign back in later."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  placeholder="dfasio"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.trim().toLowerCase())}
                  required
                />
              </label>

              <div style={{ color: colors.muted, fontSize: 12, marginTop: -6, lineHeight: 1.5 }}>
                Choose a short and memorable workspace name.
                <br />
                You will use this workspace name to sign back in later.
                <br />
                Example:{" "}
                <strong style={{ color: "#dbeafe" }}>
                  {subdomain || "dfasio"}
                </strong>
              </div>

              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Contact person
                  <span
                    style={helperStyle}
                    title="This helps identify the main person responsible for setting up and managing this workspace."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  placeholder="Your full name"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                />
              </label>

              <label style={labelStyle}>
                Contact phone
                <input
                  style={inputStyle}
                  placeholder="(555) 555-5555"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                />
              </label>

              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Business type
                  <span
                    style={helperStyle}
                    title="This helps JobTelesis understand what kind of workflows your company may need, such as construction, process serving, landscaping, or field services."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  placeholder="Construction, process serving, landscaping, etc."
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                />
              </label>

              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Owner email
                  <span
                    style={helperStyle}
                    title="This becomes the first admin login for the workspace. Use an email you can access reliably."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="admin@yourcompany.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
                  required
                />
              </label>

              <label style={labelStyle}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  Password
                  <span
                    style={helperStyle}
                    title="Create a secure password for the first workspace owner account."
                  >
                    ?
                  </span>
                </div>
                <input
                  style={inputStyle}
                  type="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>

              {!betaMode && (
                <label style={labelStyle}>
                  Subscription Plan
                  <select
                    style={inputStyle}
                    value={plan}
                    onChange={(e) => setPlan(e.target.value)}
                  >
                    <option value="basic">Basic Monthly</option>
                    <option value="pro">Pro Monthly</option>
                    <option value="enterprise">Enterprise Monthly</option>
                  </select>
                  <span style={{ color: colors.muted, fontSize: 12, fontWeight: 500 }}>
                    You will continue to Stripe Checkout to start the JobTelesis subscription trial.
                  </span>
                </label>
              )}

              <button
                type="submit"
                disabled={
                  busy ||
                  betaLoading ||
                  (!betaMode &&
                    (!betaControls.public_signup_enabled ||
                      !betaControls.workspace_creation_enabled)) ||
                  (betaMode && !betaControls.beta_program_enabled)
                }
                style={{
                  marginTop: 4,
                  width: "100%",
                  border: "none",
                  borderRadius: 12,
                  padding: "13px 16px",
                  background: colors.gold,
                  color: "#071827",
                  fontWeight: 900,
                  cursor:
                    busy ||
                    betaLoading ||
                    (!betaMode &&
                      (!betaControls.public_signup_enabled ||
                        !betaControls.workspace_creation_enabled)) ||
                    (betaMode && !betaControls.beta_program_enabled)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    busy ||
                    betaLoading ||
                    (!betaMode &&
                      (!betaControls.public_signup_enabled ||
                        !betaControls.workspace_creation_enabled)) ||
                    (betaMode && !betaControls.beta_program_enabled)
                      ? 0.65
                      : 1,
                  fontSize: 15,
                }}
              >
                {busy
                  ? betaMode ? "Creating beta workspace..." : "Opening Stripe checkout..."
                  : betaLoading
                  ? "Checking beta access..."
                  : betaMode && !betaControls.beta_program_enabled
                  ? "Beta Enrollment Closed"
                  : !betaMode && !betaControls.public_signup_enabled
                  ? "Public Signup Closed"
                  : !betaMode && !betaControls.workspace_creation_enabled
                  ? "Workspace Creation Disabled"
                  : betaMode
                  ? "Join Beta Program"
                  : "Create Workspace"}
              </button>
            </form>

            <div
              style={{
                marginTop: 16,
                color: colors.muted,
                fontSize: 12,
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              By creating a workspace, you are setting up the first owner account for your company.
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
